/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "../contexts/LocationContext";
import axios from "axios";
import { Button, Typography, Alert, Spin, message, Card, Row, Col, Statistic, Tag } from "antd";
import { DollarOutlined, ThunderboltOutlined, EnvironmentOutlined, TrophyOutlined, ArrowUpOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { config } from "../config/env";
import { savingsReasonText } from "../components/InsufficientDataCard";
import { trackExpedientPageViewed, trackSubscribeButtonClicked, trackCheckoutInitiated } from "../utils/analytics";
import ROICalculator from "../components/ROICalculator";
import BeforeAfterComparison from "../components/BeforeAfterComparison";
import PeerComparison from "../components/PeerComparison";
import ESGCredentials from "../components/ESGCredentials";
import UrgencyBanner from "../components/UrgencyBanner";
import TrustBadges from "../components/TrustBadges";

const { Title, Paragraph, Text } = Typography;

const ExpedientPage: React.FC = () => {
  const { user, setUser } = useAuth();
  const { city, state, utility, lat, lng } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [estimatedSavings, setEstimatedSavings] = useState<{
    monthlySavings: number | null;
    annualSavings: number | null;
    paybackMonths: number | null;
    roi: number | null;
  } | null>(null);
  const [loadingSavings, setLoadingSavings] = useState(true);
  const [savingsUnavailable, setSavingsUnavailable] = useState<string | null>(
    null,
  );

  useEffect(() => {
    // Track page view
    trackExpedientPageViewed(!!(lat && lng));

    const fetchSubscription = async () => {
      if (user?.id) {
        try {
          const token = localStorage.getItem("access_token");
          const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/api/subscriptions/user-subscription`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          setSubscription(response.data.subscription);
        } catch (err: any) {
          console.error("Failed to fetch subscription status:", err);
        }
      }
    };

    fetchSubscription();
  }, [user, lat, lng]);

  useEffect(() => {
    const fetchEstimatedSavings = async () => {
      if (!user?.id || !lat || !lng) {
        // P1 M&V: without a location there are no projections — never defaults.
        setEstimatedSavings(null);
        setSavingsUnavailable('Set your location to see metered projections.');
        setLoadingSavings(false);
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${config.apiUrl}/api/energy/savings/metrics?days=30`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const payload = response.data.savings;
        if (payload?.status === 'ok' && payload.metrics) {
          const m = payload.metrics;
          setEstimatedSavings({
            monthlySavings: m.dollarsSaved ?? null,
            annualSavings: m.dollarsSaved != null ? m.dollarsSaved * 12 : null,
            paybackMonths: m.paybackMonths ?? null,
            roi: m.roi ?? null,
          });
          setSavingsUnavailable(null);
        } else {
          // P1 M&V: no verified metrics → explain why, never estimate.
          setEstimatedSavings(null);
          setSavingsUnavailable(savingsReasonText(payload));
        }
      } catch (err: any) {
        console.error("Failed to fetch estimated savings:", err);
        setEstimatedSavings(null);
        setSavingsUnavailable(
          'Savings service unreachable — no figures are shown rather than estimated ones.',
        );
      } finally {
        setLoadingSavings(false);
      }
    };

    fetchEstimatedSavings();
  }, [user?.id, lat, lng]);

  const handleCancelSubscription = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/subscriptions/cancel`,
        {
          customerId: user?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );
      // Optionally, update user state or refetch subscription status
      if (user) {
        setUser({
          ...user,
          isSubscribed: false,
        });
      }
      setSubscription(null);
      navigate("/cancel");
    } catch (err: any) {
      console.error("Subscription cancellation failed:", err);
      setError("Failed to cancel subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$29',
      yearlyPrice: '$299',
      priceId: config.stripe.basicPriceId,
      features: [
        'Energy Analytics Dashboard',
        'Location-Specific Insights',
        'Utility Rate Optimization',
        'Historical Data Analysis',
        'Email Alerts',
        'Basic Reporting',
      ],
      highlight: false,
    },
    {
      name: 'Professional',
      price: '$79',
      yearlyPrice: '$799',
      priceId: config.stripe.premiumPriceId,
      features: [
        'Everything in Starter',
        'Advanced Predictive Modeling',
        'AI-Powered Recommendations',
        'Real-Time Sensor Integration',
        'Custom Dashboards',
        'Priority Support',
        'API Access',
      ],
      highlight: true,
    },
  ];

  const handleSubscribeWithPrice = async (priceId: string) => {
    setLoading(true);
    setError(null);
    
    // Track checkout initiated
    const planType = priceId.includes('basic') ? 'basic' : priceId.includes('premium') ? 'premium' : 'unknown';
    trackSubscribeButtonClicked('expedient_page', planType);
    trackCheckoutInitiated(priceId, planType);
    
    try {
      const token = localStorage.getItem("access_token");

      const statusResp = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/subscriptions/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (statusResp?.data?.isActive) {
        if (user) setUser({ ...user, isSubscribed: true });
        message.success("You already have premium features!");
        navigate("/tasks");
        return;
      }

      const checkoutResp = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/subscriptions/create-checkout-session`,
        { priceId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const url = checkoutResp?.data?.url;
      if (!url) throw new Error("Failed to create checkout session");
      window.location.href = url;
    } catch (error: any) {
      console.error("Error:", error);
      setError("Failed to enable premium features. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Render nothing if user is null or undefined (AuthContext not ready yet)
  if (!user) {
    return null;
  }

  return (
    <div style={styles.container}>
      {!user.isSubscribed ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={1} style={{ marginBottom: '16px' }}>
              Unlock Your Energy Savings Potential
            </Title>
            {city && state && (
              <Paragraph style={{ fontSize: '18px', color: '#666' }}>
                Based on your location in <strong>{city}, {state}</strong>
                {utility && ` (${utility})`}
              </Paragraph>
            )}
          </div>

          {/* Location-Specific Value Estimates */}
          {!estimatedSavings && !loadingSavings && savingsUnavailable && (
            <Alert
              message="No verified projections yet"
              description={savingsUnavailable}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
              closable
            />
          )}
          {estimatedSavings && !loadingSavings && (
            <Alert
              message="Projected Savings"
              description="These are projections based on typical usage patterns for your location. Your actual results may vary based on your specific energy usage, equipment, and implementation of recommendations."
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
              closable
            />
          )}
          {estimatedSavings && !loadingSavings && (
            <Card style={{ marginBottom: '32px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Monthly Savings</span>}
                    value={estimatedSavings.monthlySavings ?? '—'}
                    prefix={<DollarOutlined style={{ color: 'white' }} />}
                    valueStyle={{ color: 'white', fontSize: '24px' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Annual Projection</span>}
                    value={estimatedSavings.annualSavings ?? '—'}
                    prefix={<ArrowUpOutlined style={{ color: 'white' }} />}
                    valueStyle={{ color: 'white', fontSize: '24px' }}
                    formatter={(value) => (typeof value === 'number' ? formatCurrency(value) : '—')}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>Payback Period</span>}
                    value={estimatedSavings.paybackMonths ?? '—'}
                    suffix="months"
                    prefix={<TrophyOutlined style={{ color: 'white' }} />}
                    valueStyle={{ color: 'white', fontSize: '24px' }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title={<span style={{ color: 'white' }}>ROI</span>}
                    value={estimatedSavings.roi ?? '—'}
                    suffix="%"
                    prefix={<ThunderboltOutlined style={{ color: 'white' }} />}
                    valueStyle={{ color: 'white', fontSize: '24px' }}
                  />
                </Col>
              </Row>
              {city && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                    Projected savings for users in <strong>{city}</strong>:{' '}
                    {estimatedSavings.monthlySavings != null
                      ? `${formatCurrency(estimatedSavings.monthlySavings)}/month`
                      : '—'}
                  </Text>
                </div>
              )}
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                  * Projections based on typical usage. Your actual results may vary.
                </Text>
              </div>
            </Card>
          )}

          {loadingSavings && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Spin size="large" tip="Calculating your potential savings..." />
            </div>
          )}

                 {error && (
                   <Alert message={error} type="error" showIcon style={styles.alert} />
                 )}

                 {/* Urgency Banner */}
                 <UrgencyBanner />

                 {/* Risk Reversal Banner */}
                 <Alert
                   message="30-Day Money-Back Guarantee"
                   description="Try Premium risk-free. If you don't save at least $500 in the first 30 days, we'll refund your subscription and you keep access. No questions asked."
                   type="success"
                   showIcon
                   style={{ marginBottom: '32px', fontSize: '16px' }}
                   icon={<CheckCircleOutlined />}
                 />

                 {/* Before/After Comparison */}
                 <div style={{ marginBottom: '32px' }}>
                   <BeforeAfterComparison />
                 </div>

                 {/* ROI Calculator */}
                 <div style={{ marginBottom: '32px' }}>
                   <ROICalculator />
                 </div>

                 {/* Pricing Tiers */}
                 <div id="pricing-section" style={{ marginBottom: '32px' }}>
                   <Title level={3} style={{ textAlign: 'center', marginBottom: '24px' }}>
                     Choose Your Plan
                   </Title>
            <Row gutter={[24, 24]} justify="center">
              {pricingTiers.map((tier) => (
                <Col xs={24} md={12} key={tier.name}>
                  <Card
                    style={{
                      height: '100%',
                      border: tier.highlight ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      boxShadow: tier.highlight ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                    }}
                    title={
                      <div style={{ textAlign: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>{tier.name}</Title>
                        {tier.highlight && (
                          <Tag color="blue" style={{ marginTop: '8px' }}>Most Popular</Tag>
                        )}
                      </div>
                    }
                    extra={
                      <div style={{ textAlign: 'center' }}>
                        <Text strong style={{ fontSize: '24px' }}>{tier.price}</Text>
                        <Text type="secondary">/month</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {tier.yearlyPrice}/year (save 15%)
                        </Text>
                      </div>
                    }
                  >
                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                      {tier.features.map((feature, idx) => (
                        <li key={idx} style={{ marginBottom: '12px' }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      type={tier.highlight ? 'primary' : 'default'}
                      block
                      size="large"
                      onClick={() => handleSubscribeWithPrice(tier.priceId)}
                      loading={loading}
                      style={{
                        marginTop: 'auto',
                        height: '48px',
                        fontSize: '16px',
                      }}
                    >
                      {loading ? <Spin /> : `Subscribe to ${tier.name}`}
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>


                 {/* Peer Comparison */}
                 <div style={{ marginBottom: '32px' }}>
                   <PeerComparison />
                 </div>

                 {/* Free Trial Notice */}
                 <Alert
                   message="Start with a Free Trial"
                   description="All plans include full access to premium features for the first 14 days. No credit card required to start. Cancel anytime."
                   type="info"
                   showIcon
                   style={{ marginBottom: '24px' }}
                 />

                 {/* Trust & Authority */}
                 <div style={{ marginBottom: '32px' }}>
                   <TrustBadges />
                 </div>

                 {/* ESG Credentials */}
                 <div style={{ marginBottom: '32px' }}>
                   <ESGCredentials />
                 </div>

                 {/* Value Proposition */}
                 <Card style={{ marginBottom: '24px' }}>
                   <Title level={4}>What You Get</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'start', marginBottom: '16px' }}>
                  <ThunderboltOutlined style={{ fontSize: '24px', color: '#1890ff', marginRight: '12px', marginTop: '4px' }} />
                  <div>
                    <Text strong>Real-Time Energy Optimization</Text>
                    <br />
                    <Text type="secondary">Monitor and optimize your energy consumption with AI-powered insights</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'start', marginBottom: '16px' }}>
                  <EnvironmentOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '12px', marginTop: '4px' }} />
                  <div>
                    <Text strong>Location-Specific Intelligence</Text>
                    <br />
                    <Text type="secondary">Get insights tailored to your utility rates and regional factors</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'start', marginBottom: '16px' }}>
                  <DollarOutlined style={{ fontSize: '24px', color: '#faad14', marginRight: '12px', marginTop: '4px' }} />
                  <div>
                    <Text strong>Proven ROI</Text>
                    <br />
                    <Text type="secondary">
                      {estimatedSavings?.paybackMonths != null
                        ? `Average payback period: ${estimatedSavings.paybackMonths.toFixed(1)} months`
                        : 'Average payback period: awaiting metered data'}
                    </Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'start', marginBottom: '16px' }}>
                  <TrophyOutlined style={{ fontSize: '24px', color: '#722ed1', marginRight: '12px', marginTop: '4px' }} />
                  <div>
                    <Text strong>Data-Driven Decisions</Text>
                    <br />
                    <Text type="secondary">Learn from historical patterns and maximize your operational efficiency</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </>
      ) : (
        <>
          <Paragraph>You are currently subscribed.</Paragraph>
          <Paragraph>
            Next Billing Date:{" "}
            {subscription?.currentPeriodEnd
              ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
              : "N/A"}
          </Paragraph>
          <Button
            type="primary"
            onClick={handleCancelSubscription}
            loading={loading}
            style={styles.button}
          >
            {loading ? <Spin /> : "Cancel Subscription"}
          </Button>
          <Paragraph style={styles.link}>
            Access your tasks <a href="/tasks">here</a>.
          </Paragraph>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: "1200px",
    margin: "50px auto",
    padding: "20px",
  },
  button: {
    marginTop: "20px",
    width: "100%",
  },
  alert: {
    marginBottom: "20px",
  },
  link: {
    marginTop: "30px",
  },
};

export default ExpedientPage;
